// The postMessage protocol with the embedding page. The window is injected, so origin rules are tested without a browser.
import { EmbedFromParent, type EmbedToParent } from "@imagora/contracts";

export interface BridgeWindow {
  parent: unknown;
  document?: { referrer: string };
  location?: { ancestorOrigins?: { length: number; [index: number]: string } };
  addEventListener(type: "message", listener: (event: BridgeMessageEvent) => void): void;
  removeEventListener(type: "message", listener: (event: BridgeMessageEvent) => void): void;
}

export interface BridgeMessageEvent {
  source: unknown;
  origin: string;
  data: unknown;
}

interface ParentWindow {
  postMessage(message: unknown, targetOrigin: string): void;
}

export type MessageHandler = (message: EmbedFromParent) => void;

export interface HostBridge {
  readonly embedded: boolean;
  /** Called with `allowedOrigins` from the exchange. False when the page embedding us is not one of them. */
  trust(allowedOrigins: ReadonlyArray<string>): boolean;
  send(message: EmbedToParent): void;
  /** The one message allowed before the origins are known. It carries nothing the parent could not already know. */
  askForToken(reason: "boot" | "expiring" | "expired"): void;
  subscribe(handler: MessageHandler): () => void;
  destroy(): void;
}

function originOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/** What the browser says about who embeds us. Null when it says nothing, as with a no-referrer iframe in Firefox. */
function declaredParentOrigin(win: BridgeWindow): string | null {
  const ancestors = win.location?.ancestorOrigins;
  if (ancestors && ancestors.length > 0) return ancestors[0] ?? null;
  return originOf(win.document?.referrer);
}

export function createHostBridge(win: BridgeWindow): HostBridge {
  const embedded = win.parent !== win;
  const parent = win.parent as ParentWindow;
  const handlers = new Set<MessageHandler>();
  let allowed: ReadonlySet<string> | null = null;
  let target: string | null = null;

  const listener = (event: BridgeMessageEvent) => {
    if (!embedded || event.source !== win.parent) return;
    const parsed = EmbedFromParent.safeParse(event.data);
    if (!parsed.success) return;
    if (allowed === null) {
      // No session yet, so no list to check against. A token is safe to take: the API verifies its signature, and `trust`
      // then checks this parent against the host the token belongs to. Everything else waits.
      if (parsed.data.type !== "imagora:token") return;
    } else {
      if (!allowed.has(event.origin)) return;
      target ??= event.origin;
    }
    for (const handler of handlers) handler(parsed.data);
  };
  win.addEventListener("message", listener);

  return {
    embedded,
    trust(allowedOrigins) {
      allowed = new Set(allowedOrigins);
      if (!embedded) return true;
      const declared = declaredParentOrigin(win);
      if (declared) {
        target = allowed.has(declared) ? declared : null;
        return target !== null;
      }
      // The browser did not say. One candidate is the answer; with several, the parent's first message settles it.
      target = allowedOrigins.length === 1 ? allowedOrigins[0]! : null;
      return allowedOrigins.length > 0;
    },
    send(message) {
      if (embedded && target) parent.postMessage(message, target);
    },
    askForToken(reason) {
      if (embedded) parent.postMessage({ type: "imagora:need-token", reason } satisfies EmbedToParent, target ?? "*");
    },
    subscribe(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    destroy() {
      win.removeEventListener("message", listener);
      handlers.clear();
    },
  };
}
